/**
 * Semantic linter rules for PhysicalBone2D: the three tree checks of
 * `PhysicalBone2D::get_configuration_warnings` (scene/2d/physics/physical_bone_2d.cpp:109-126).
 * Each is a configuration warning, so each warns here. linterParser.ts does the format checks.
 */
import { ruleRegistry } from '../../../../linter/RuleRegistry.js';
import { parentTypeVerdict, searchAncestors } from '../../../../linter/parentType.js';
import { hasChildOfType } from '../../../../linter/childType.js';
import { descendsFromClass } from '../../../../godot/classdb/classBaseTypes.js';
import { ruleInt } from '../../../../godot/variant/intSlots.js';
import { armEmits, groundedArm, reportArm } from '../../../../linter/ruleArms.js';
const arms = {
    missingSkeletonParent: groundedArm('physicalbone2d-missing-skeleton-parent', {
        kind: 'configuration-warning',
    }),
    missingBoneIndex: groundedArm('physicalbone2d-missing-bone-index', { kind: 'configuration-warning' }),
    missingJointChild: groundedArm('physicalbone2d-missing-joint-child', { kind: 'configuration-warning' }),
};
/**
 * physical_bone_2d.cpp:79-95 `_find_skeleton_parent()`: walks up a chain of PhysicalBone2D
 * ancestors to a Skeleton2D. Any other type stops the walk. The casts are `cast_to`, so
 * subclasses count (`descendsFromClass`).
 */
function skeletonAncestry(scene, node) {
    const search = searchAncestors(scene, node, (ancestor) => {
        if (descendsFromClass(ancestor.type, 'Skeleton2D'))
            return 'found';
        if (!descendsFromClass(ancestor.type, 'PhysicalBone2D'))
            return 'absent';
        return undefined;
    });
    // `unknowable` is not `absent`: an ancestor whose class its instanced scene declares could be
    // the Skeleton2D, the next bone or the terminator, and ruling all three out warns on every
    // rig built that way.
    if (search.kind === 'unknowable')
        return 'unknowable';
    return search.kind === 'found' ? search.value : 'absent';
}
function checkPhysicalBone2D(context) {
    const diagnostics = [];
    const { node, scene } = context;
    const ancestry = skeletonAncestry(scene, node);
    // `unknowable` answers neither warning: the first needs to know the ancestor
    // is not a Skeleton2D, the second needs to know it is.
    if (ancestry === 'absent') {
        reportArm(diagnostics, arms.missingSkeletonParent, node, `PhysicalBone2D '${node.name}' has no Skeleton2D ancestor. A PhysicalBone2D only works with a Skeleton2D or another PhysicalBone2D as a parent node.`);
    }
    else if (ancestry === 'found') {
        // Absent, or present but unreadable, both mean no index is assigned.
        const boneIndex = ruleInt(node.rawProperties.bone2d_index ?? '') ?? -1;
        if (!(boneIndex > -1)) {
            reportArm(diagnostics, arms.missingBoneIndex, node, `PhysicalBone2D '${node.name}' has no bone2d_index assigned. A PhysicalBone2D needs to be assigned to a Bone2D node (set bone2d_index) in order to function.`);
        }
    }
    // `cast_to<PhysicalBone2D>(get_parent())` (physical_bone_2d.cpp:119), so
    // subclasses count and an unseeable parent decides nothing.
    if (parentTypeVerdict(scene, node, 'PhysicalBone2D').kind === 'satisfied') {
        // The "Joint2D-based child" physical_bone_2d.cpp:118-122 asks for, a `cast_to<Joint2D>`.
        if (!hasChildOfType(node, ['Joint2D'])) {
            reportArm(diagnostics, arms.missingJointChild, node, `PhysicalBone2D '${node.name}' is chained under another PhysicalBone2D but has no Joint2D-based child. A PhysicalBone2D node should have a Joint2D-based child node to keep bones connected.`);
        }
    }
    return diagnostics;
}
const physicalBone2DValidationRule = {
    meta: {
        name: 'valid-physicalbone2d',
        description: 'Validates PhysicalBone2D scene-context rules mirrored from get_configuration_warnings: Skeleton2D/PhysicalBone2D ancestry, an assigned bone2d_index, and a Joint2D child when chained under another PhysicalBone2D',
        category: 'validation',
        applicableNodeTypes: ['PhysicalBone2D'],
        emits: armEmits(arms),
    },
    check: checkPhysicalBone2D,
};
ruleRegistry.register(physicalBone2DValidationRule);
//# sourceMappingURL=linter.js.map